/**
 * Query-key derivation for correspondence-API hooks.
 *
 * Provider-prefixed keys (`['gmail', …]` / `['outlook', …]`) must stay
 * derivable here because `invalidateEmailCaches` in
 * `frontend/web/src/lib/supabase-utils.ts` invalidates by those exact prefixes.
 * Dropping them silently breaks cache invalidation for every legacy
 * cache entry that was written under those keys.
 */

import type { QueryKey } from '@tanstack/react-query';
import type { Provider, ReadAction } from '@/lib/correspondence/contracts';

/**
 * Provider hint threaded into a query key. Callers that know the target
 * provider (e.g. a pilot migration of a Gmail-specific hook) pass it
 * explicitly so the key matches the legacy shape byte-for-byte. Callers
 * that don't know the provider (fan-out reads, capability map) fall
 * through to `'correspondence'`.
 */
export type QueryKeyProviderHint = Provider | 'correspondence';

export interface QueryKeyInputShape {
  messageId?: string;
  threadId?: string;
  organizationId?: string;
  accountId?: string;
}

/**
 * Builds the TanStack Query key for a given action + input. Callers MAY
 * override via `options.queryKey` when they need a specific legacy shape
 * (e.g. the pilot's `getGmailMessageQueryKey`). Unit tests verify the
 * default shape for each action family.
 */
export function queryKeyForAction(
  action: ReadAction,
  input: QueryKeyInputShape,
  providerHint: QueryKeyProviderHint = 'correspondence',
): QueryKey {
  const orgId = input.organizationId;
  const accountId = input.accountId;

  // Grouped by query-key shape to keep the branch count under the
  // sonarjs max-switch-cases budget. Each group within the same block
  // maps to the same key shape, as documented inline.
  if (action === 'get_capabilities') {
    return ['correspondence', 'capabilities'];
  }
  if (action === 'get_message') {
    return [providerHint, 'message', input.messageId, orgId];
  }
  if (action === 'get_thread') {
    return [providerHint, 'thread', input.threadId, orgId];
  }
  if (action === 'get_attachment') {
    return [providerHint, 'attachment', input.messageId, orgId];
  }
  // Default shape for the rest: [provider, <namespace>, orgId, accountId].
  const NAMESPACE_BY_ACTION: Record<
    Exclude<ReadAction, 'get_capabilities' | 'get_message' | 'get_thread' | 'get_attachment'>,
    string
  > = {
    list_messages: 'messages',
    search_messages: 'messages',
    list_labels: 'labels',
    get_profile: 'profile',
    get_profile_photo: 'profile-photo',
    get_profile_name: 'profile-name',
    get_signature: 'signature',
    list_history: 'history',
  };
  return [providerHint, NAMESPACE_BY_ACTION[action], orgId, accountId];
}
